import { useCallback, useEffect, useState } from 'react';
import { QTasksDrawerProps, UseTasksDrawerEventListenerResult } from './types';

// Event key constants
const TASKS_DRAWER_EVENT_KEY_UPDATE = 'QTasksDrawer-update';
const TASKS_DRAWER_EVENT_KEY_CLOSE = 'QTasksDrawer-closed';

/**
 * Empty drawer props constant used to represent a closed drawer.
 */
const EMPTY_DRAWER_PROPS = {
  taskId: undefined,
  viewOnly: undefined,
} as const satisfies QTasksDrawerProps;

/**
 * Hook for consuming applications to listen for Tasks drawer events.
 *
 * This hook should be used in the host application to receive task drawer
 * open/close requests and render the actual drawer UI.
 *
 * @returns Drawer props including isOpen state and onClose handler
 *
 * @example
 * ```tsx
 * // In the host application
 * const TasksDrawerContainer = () => {
 *   const drawerProps = useTasksDrawerEventListener();
 *
 *   return (
 *     <QDrawer isOpen={drawerProps.isOpen} onClose={drawerProps.onClose}>
 *       {drawerProps.taskId && (
 *         <TaskDetailsContent
 *           taskId={drawerProps.taskId}
 *           isLoading={drawerProps.isLoading}
 *         />
 *       )}
 *     </QDrawer>
 *   );
 * };
 * ```
 */
export const useTasksDrawerEventListener =
  (): UseTasksDrawerEventListenerResult => {
    const [props, setProps] = useState<QTasksDrawerProps>(EMPTY_DRAWER_PROPS);

    // Listen for drawer update events
    useEffect(() => {
      const invokeCallback = (e: CustomEvent<QTasksDrawerProps>) => {
        setProps(e.detail);
      };

      window.addEventListener(TASKS_DRAWER_EVENT_KEY_UPDATE, invokeCallback);

      return () =>
        window.removeEventListener(
          TASKS_DRAWER_EVENT_KEY_UPDATE,
          invokeCallback,
        );
    }, [setProps]);

    // Close handler dispatches close event and resets props
    const onClose = useCallback(() => {
      setProps(EMPTY_DRAWER_PROPS);
      closeDrawer();
    }, [setProps]);

    return {
      ...props,
      isOpen: !!props.taskId,
      onClose,
    };
  };

/**
 * Hook to dispatch events to open/close the Tasks drawer in the host application.
 *
 * If `props` is defined and the `taskId` field is truthy, the drawer will open.
 * If `props` is null or the `taskId` field is falsy, the drawer will close.
 *
 * @param props - The properties to display in the Tasks drawer. Pass null to close.
 * @param onRemoteClose - Optional callback invoked when drawer is closed by another component
 *
 * @example
 * ```tsx
 * // In TaskDetail component
 * const TaskDetail = () => {
 *   const { taskId } = useParams();
 *   const { task, isLoading } = useRetrieveTask(taskId);
 *
 *   useTasksDrawer(
 *     taskId ?? null
 *   );
 *
 *   return null;
 * };
 * ```
 */
export const useTasksDrawer = (
  props: QTasksDrawerProps | null,
  onRemoteClose?: () => void,
): void => {
  // Notify the host frontend to update the drawer whenever props change
  useEffect(() => {
    console.log('Requesting that host frontend update the tasks drawer', props);

    const detail = props ? props : EMPTY_DRAWER_PROPS;

    window.dispatchEvent(
      new CustomEvent(TASKS_DRAWER_EVENT_KEY_UPDATE, {
        detail,
      }),
    );
  }, [JSON.stringify(props)]);

  // Subscribe to notifications from elsewhere that the drawer has been closed
  useEffect(() => {
    if (!onRemoteClose) {
      return;
    }

    const cb = onRemoteClose;
    window.addEventListener(TASKS_DRAWER_EVENT_KEY_CLOSE, cb);

    return () => window.removeEventListener(TASKS_DRAWER_EVENT_KEY_CLOSE, cb);
  }, [onRemoteClose]);
};

/**
 * Internal helper to dispatch the drawer close event.
 */
const closeDrawer = () => {
  window.dispatchEvent(new CustomEvent(TASKS_DRAWER_EVENT_KEY_CLOSE));
};
